import { domToPng, domToJpeg, domToBlob, domToSvg } from "modern-screenshot"

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function createCopy(object) {
  return JSON.parse(JSON.stringify(object))
}

export function createMarkup(string) {
  return { __html: string }
}

export function downloadCSV(data, fileName, options = {}) {
  const {
    delimiter = ",",
    headerKeys = [],
    excludedKeys = [],
    dataFormatter = {}, // e.g. { time_gmt: (value) => moment(value).format("MM-DD-YYYY HH:mm") }
    headerFormatter = {}, // e.g. { nen3610id: "code" }
    columnOrder = [], // e.g. ["id", "code", "objecttype", "rating", "summary"]
  } = options

  if (!Array.isArray(data) || data.length === 0) {
    console.warn("downloadCSV: No data provided.")
    return
  }

  // ---- Determine which headers to use ----
  let header = Object.keys(data[0])

  if (headerKeys.length > 0) {
    // use only specific headers (ignore excludedKeys)
    header = headerKeys.filter(h => header.includes(h))
  } else if (excludedKeys.length > 0) {
    // exclude certain keys
    header = header.filter(h => !excludedKeys.includes(h))
  }

  // ---- Apply columnOrder if provided ----
  if (columnOrder.length > 0) {
    const lowerColumnOrder = columnOrder.map(c => c.toLowerCase())

    // Match headers in the desired order, case-insensitively, preserving original casing
    const orderedHeaders = []
    const remainingHeaders = [...header]

    lowerColumnOrder.forEach(col => {
      const matchIndex = remainingHeaders.findIndex(h => h.toLowerCase() === col)
      if (matchIndex !== -1) {
        orderedHeaders.push(remainingHeaders[matchIndex])
        remainingHeaders.splice(matchIndex, 1)
      }
    })

    // Append the rest of the columns that weren't specified
    header = [...orderedHeaders, ...remainingHeaders]
  }

  // ---- Escape and quote CSV values ----
  const escapeCSV = (value) => {
    if (value === null || value === undefined) return ""
    let str = String(value).replace(/"/g, '""')
    if (str.includes(delimiter) || str.includes("\n") || str.includes('"')) {
      str = `"${str}"`
    }
    return str
  }

  // ---- Build header row ----
  const headerRow = header.map(h => escapeCSV(headerFormatter[h] || h)).join(delimiter)

  // ---- Build data rows ----
  const dataRows = data.map(row =>
    header.map(fieldName => {
      const value = row[fieldName]
      // Apply data formatter if available
      const formattedValue =
        typeof dataFormatter[fieldName] === "function"
          ? dataFormatter[fieldName](value, row)
          : value
      return escapeCSV(formattedValue)
    }).join(delimiter)
  )

  // ---- Compose CSV content ----
  const csvLines = [
    `sep=${delimiter}`,
    headerRow,
    ...dataRows
  ]

  const csvContent = "\uFEFF" + csvLines.join("\r\n")

  // ---- Trigger download ----
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = fileName.includes(".") ? fileName : `${fileName}.csv`

  document.body.appendChild(link)
  link.click()

  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function downloadData(content, fileName, options = {}) {
  const {
    type = "application/json" // e.g. { type: "text/plain;charset=utf-8" }
  } = options

  let blob
  if (options.type === "blob") {
    blob = content
  } else {
    blob = new Blob([content], { type: type })
  }

  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = fileName.includes(".") ? fileName : `${fileName}.json`

  document.body.appendChild(link)
  link.click()

  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function isNullOrEmpty(value) {
  return value === null || value === undefined || value.toString().trim() === ""
}

export function isValidHttpUrl(string) {
  let url

  try {
    url = new URL(string)
  } catch (_) {
    return false
  }

  return url.protocol === "http:" || url.protocol === "https:"
}

// A failed request answers with JSON, an OGC/GeoServer XML exception report, an HTML error
// page, or nothing at all. This turns any of those into one readable line.
export function describeResponseBody(body, depth = 0) {
  const text = typeof body === "string" ? body.trim() : ""
  if (!text) return null

  try {
    const json = JSON.parse(text)
    const nested = json?.detail || json?.error || json?.message

    if (!nested) return text

    // A proxy wraps the upstream error in a field of its own, and that value can be XML
    // or JSON again - the FEWS proxy answers JSON with an XML report inside "detail".
    // Keep unwrapping until plain text is left, with a depth guard against a cycle.
    if (depth >= 3) return nested

    return describeResponseBody(nested, depth + 1) || nested
  } catch (error) {
    // Not JSON, try the other shapes below
  }

  // GeoServer answers an unknown layer with this report and an HTTP 200. The lookahead
  // keeps the match off the <ServiceExceptionReport> wrapper around it.
  const serviceException = text.match(/<ServiceException(?=[\s>])[^>]*>([\s\S]*?)<\/ServiceException>/i)
  if (serviceException) return serviceException[1].replace(/\s+/g, " ").trim()

  if (text.startsWith("<")) {
    // An error page states its problem in the title or first heading; the body is mostly
    // styling, which would drown the message in noise
    const heading = text.match(/<title[^>]*>([\s\S]*?)<\/title>/i) || text.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i)
    if (heading) {
      const headingText = heading[1].replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
      if (headingText) return headingText
    }

    const stripped = text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
    return stripped.slice(0, 200) || null
  }

  return text.slice(0, 300)
}

// Server messages carry a stack trace or a "Details:" tail that a user cannot act on.
// This keeps the one sentence that says what went wrong, for display purposes only -
// the full text stays available in the error report.
export function summarizeMessage(message, maxLength = 160) {
  const text = String(message ?? "").replace(/\s+/g, " ").trim()
  if (!text) return ""

  // Drop a leading "Error: " from Error.toString(), it adds nothing next to the message
  let summary = text.replace(/^Error: /, "")

  // Cut at a "Details:" tail or the first stack frame ("at org.geoserver.platform...")
  summary = summary.split(/ Details:| at (?:[a-zA-Z0-9_$]+\.){2,}/)[0].trim()

  // Prefer the first sentence; the lookahead keeps version numbers and decimals intact
  const firstSentence = summary.match(/^.*?[.!?](?=\s|$)/)
  if (firstSentence && firstSentence[0].length >= 20) {
    summary = firstSentence[0]
  }

  if (summary.length <= maxLength) return summary

  return `${summary.slice(0, maxLength - 1).trimEnd()}…`
}

// Never returns an empty message: an empty one used to travel on as a falsy "exception",
// slip past the callers' error checks and surface as an unreadable JSON.parse failure
export function describeRequestFailure(status, statusText, body) {
  const described = describeResponseBody(body)
  if (described) return described

  // Status 0 means the browser never got a response it was allowed to read - a network
  // failure, or a response without CORS headers (error pages often lack them)
  if (!status) return "The request could not be completed: no readable response from the server"

  return `Request failed with status ${status}${statusText ? ` (${statusText})` : ""}`
}

export async function makeRequest(url, functionName, method, signal, token, parameters, body, requestHeaders, responseType) {
  return await new Promise(function (resolve, reject) {
    const xhr = new XMLHttpRequest()

    if ("withCredentials" in xhr) {
      xhr.open(method, url, true)

      token && xhr.setRequestHeader("Authorization", `Bearer ${token}`)
      xhr.setRequestHeader("Function", functionName)
      xhr.setRequestHeader("Parameters", JSON.stringify(parameters))

      if (requestHeaders) {
        Object.entries(requestHeaders).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value)
        })
      }
      if (responseType) {
        xhr.responseType = responseType
      }
    }

    // Response headers are only readable when the server exposes them through CORS,
    // so every read is optional and must never break the request itself
    const readHeader = (name) => {
      try {
        return xhr.getResponseHeader(name)
      } catch (error) {
        return null
      }
    }

    xhr.onload = function () {
      if (this.status >= 200 && this.status < 300) {
        resolve(xhr.response)
      } else {
        // The status travels with the exception: the UI needs it to tell a rate
        // limit (429) apart from an actual failure
        resolve({
          exception: describeRequestFailure(this.status, this.statusText, xhr.response),
          status: this.status,
          retryAfter: readHeader("retry-after"),
          rateLimitReset: readHeader("x-ratelimit-reset")
        })
      }
    }

    xhr.onerror = function () {
      resolve({
        exception: describeRequestFailure(this.status, this.statusText, xhr.response),
        status: this.status
      })
    }

    // claude: geen timeout — onabort vuurt alleen als de request bewust is afgebroken (bv. bij het sluiten
    // van de pagina). "aborted" laat catchMessage dit stil negeren i.p.v. een foutmelding te tonen (HSGA-204)
    xhr.onabort = function () {
      resolve({ exception: "Request aborted", aborted: true })
    }

    xhr.withCredentials = false

    if (body instanceof FormData) {
      xhr.send(body)
    } else {
      xhr.send(JSON.stringify(body))
    }

    if (signal) {
      signal.addEventListener("abort", function () {
        if (signal.aborted) {
          xhr.abort()
        }
      })
    }
  })
}

export async function screenshotElement({
  selector,
  element,
  filename = "screenshot",
  type = "png",
  quality = 0.92,
  scale = 2,
  bgcolor,
  width,
  height,
  filter,
  style,
  download = true,
  fullPage = false,        // capture the whole viewport (incl. MUI portals)
  nowrap = true,           // force white-space: nowrap during capture
} = {}) {
  // ── 1. Resolve target node ────────────────────────────────────────────────
  let node = element;

  if (fullPage) {
    node = document.documentElement;   // includes <body> + all portaled dialogs
  } else if (selector) {
    node = document.querySelector(selector);
    if (!node) {
      throw new Error(`screenshotElement: no element found for selector "${selector}"`);
    }
  }

  if (!node) {
    throw new Error(
      "screenshotElement: provide either a `selector`, `element`, or set `fullPage: true`."
    );
  }

  // ── 2. Wait for fonts (prevents text reflow / wrong-font wrapping) ───────
  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  // Right after step 2 (font wait), before computing dimensions:
  await new Promise(resolve => requestAnimationFrame(() =>
    requestAnimationFrame(resolve)
  ));

  // ── 3. Compute dimensions ────────────────────────────────────────────────
  let layoutWidth, layoutHeight;
  if (fullPage) {
    layoutWidth = width ?? window.innerWidth;
    layoutHeight = height ?? window.innerHeight;
  } else {
    const rect = node.getBoundingClientRect();
    // +1 to avoid sub-pixel rounding wrapping text on spaces
    layoutWidth = width ?? Math.ceil(rect.width) + 1;
    layoutHeight = height ?? Math.ceil(rect.height) + 1;
  }

  // ── 4. Inject temporary nowrap stylesheet ────────────────────────────────
  // Forces white-space: nowrap on the captured subtree and all descendants
  // to prevent spurious text wrapping in the cloned render.
  let tempStyleTag = null;
  if (nowrap) {
    tempStyleTag = document.createElement('style');
    tempStyleTag.setAttribute('data-screenshot-temp', 'true');
    tempStyleTag.textContent = `
      .__screenshot-nowrap, .__screenshot-nowrap * {
        white-space: nowrap !important;
      }
    `;
    document.head.appendChild(tempStyleTag);
    node.classList.add('__screenshot-nowrap');

    // For fullPage captures, MUI dialogs are portaled to <body> and won't be
    // descendants of <html>'s class scope in the way the selector expects —
    // but since the class is on <html>, every element in the document is a
    // descendant, so the universal selector still catches them.
  }

  // ── 5. Build options ─────────────────────────────────────────────────────
  const options = {
    font: {},
    width: layoutWidth,
    height: layoutHeight,
    scale,                            // modern-screenshot handles HiDPI natively
    backgroundColor: bgcolor,         // note: renamed from `bgcolor`
    quality: type === "jpeg" ? quality : undefined,
    style: {
      ...style,
      ...(fullPage && {
        margin: 0,
        padding: 0,
        transform: 'none',
      }), // kill phantom top margin on body/html
    },
    // Combined filter: skip cross-origin stylesheets (Google Fonts CORS noise)
    // + skip tainted canvases (the toDataURL error you just hit)
    // + honor user-supplied filter
    filter: (n) => {
      if (n.tagName === 'CANVAS') {
        try { n.toDataURL(); } catch { return false; }
      }
      return filter ? filter(n) : true;
    },
    // Ignore CORS errors on individual resources instead of failing the whole capture
    onCloneNode: (cloned) => cloned,
    fetch: {
      requestInit: { cache: 'no-cache' },
      bypassingCache: true,
    },
  };

  // ── 6. Capture ───────────────────────────────────────────────────────────
  let result;
  try {
    switch (type) {
      case "jpeg":
        result = await domToJpeg(node, options);
        break;
      case "svg":
        result = await domToSvg(node, options);
        break;
      case "blob":
        result = await domToBlob(node, options);
        break;
      case "png":
      default:
        result = await domToPng(node, options);
        break;
    }
  } finally {
    // ── 7. Clean up nowrap styling ─────────────────────────────────────────
    if (nowrap) {
      node.classList.remove('__screenshot-nowrap');
      if (tempStyleTag) tempStyleTag.remove();
    }
  }

  // ── 8. Download ──────────────────────────────────────────────────────────
  if (download) {
    const ext = type === "jpeg" ? "jpg" : type === "blob" ? "png" : type;
    const link = document.createElement("a");
    link.download = `${filename}.${ext}`;

    if (type === "blob") {
      link.href = URL.createObjectURL(result);
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 10_000);
    } else {
      link.href = result;
      link.click();
    }
  }

  return result;
}

export function sortByKey(array, key, desc) {
  const sorted = array.slice().sort((a, b) => {
    const x = a[key]
    const y = b[key]

    // Handle undefined, null, or empty values — push them to the bottom
    if (x == null || x === "") return 1  // a goes after b
    if (y == null || y === "") return -1 // b goes after a

    // Normal comparison
    if (x < y) return -1
    if (x > y) return 1
    return 0
  })

  if (desc === "desc") return sorted.reverse()
  return sorted
}